import { useMutation, UseMutationResult, useQueryClient } from 'react-query';

import { getTokenWithType } from './useAuth';

import { saveImportAccount } from 'utils/urls';

import {
  ImportAccount,
  CopyAccountOutput as ImportAccountOutput,
} from 'types/accounts';
import { ErrorModal } from 'types/modalTypes';

export async function postImportAccount({
  params,
  currentAccount,
}: TVariables): Promise<TResult> {
  const response = await fetch(saveImportAccount(currentAccount), {
    method: 'POST',
    headers: {
      Authorization: getTokenWithType(),
      'content-type': 'application/json',
    },
    body: JSON.stringify(params),
  });
  return response.json();
}

type TError = ErrorModal;

type TResult = {
  Data: ImportAccountOutput;
  IsSuccess: boolean;
  StatusCode: number;
} & ErrorModal;

type TVariables = {
  params: ImportAccount;
  currentAccount: number;
};

type TSnapshot = unknown;

function useSaveImportAccount(): UseMutationResult<
  TResult,
  TError,
  TVariables,
  TSnapshot
> {
  const queryClient = useQueryClient();
  return useMutation(async ({ params, currentAccount }: TVariables) => {
    const newAccount = await postImportAccount({
      params,
      currentAccount,
    });

    if (newAccount.StatusCode === 200) {
      queryClient.invalidateQueries(
        ['get_procedure_builder_procedures', newAccount.Data.Id],
        {
          refetchInactive: true,
        },
      );
      queryClient.invalidateQueries(
        ['get_procedures_url_list', newAccount.Data.Id],
        {
          refetchInactive: true,
        },
      );
      queryClient.invalidateQueries('get_officeFacts', {
        refetchInactive: true,
      });
      queryClient.invalidateQueries('locations_list', {
        refetchInactive: true,
      });
      queryClient.invalidateQueries('contact_list', {
        refetchInactive: true,
      });
      queryClient.invalidateQueries('get_custom_table', {
        refetchInactive: true,
      });
      queryClient.invalidateQueries('get_tag_mappings', {
        refetchInactive: true,
      });
      queryClient.invalidateQueries('get_ftp_settings', {
        refetchInactive: true,
      });
      queryClient.invalidateQueries('forms_list', {
        refetchInactive: true,
      });

      return newAccount;
    }
    if (newAccount.Errors[0].Message) throw newAccount;

    return newAccount;
  });
}

export { useSaveImportAccount };
